import BigNumber from 'bignumber.js'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import { StyledControlContainer } from 'UIKit/FormControl/styled'
import { InputMessage } from 'UIKit/Input/styled'
import Skeleton from 'UIKit/Skeleton'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { NETWORK_MAP } from 'config/constants/network'
import { TokenAmount } from 'config/types'
import { ValidationError } from 'config/types/validator'
import useKeydownEventListener from 'hooks/useKeydownEventListener'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import { useCallback, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import { useTokenSelected } from 'state/session/hooks'
import styled, { css } from 'styled-components'
import { getFullDisplayBalance } from 'utils/formatBalance'
import GameInfoMessage from 'views/GameDetails/GameInfoMessage'
import { InHouseGameNumericalInput, InHouseLabel, InhouseGameInputContainer } from 'views/InhouseGame/components/styled'
import { useFormBetAmount } from './hooks'

const BetAmount: React.FC<
  {
    setBetTokenAmount: (amount: TokenAmount) => void
    setIsAmountInvalid: (value: boolean) => void
    disabled?: boolean
    maxButton?: boolean
    hotKey?: boolean
    isBlackListToken?: boolean
    updateBetAmountRef?: { current?: (amount: BigNumber) => void }
    hideEstToToken?: boolean

    inputId?: string
    maxBet: number
  } & FlexProps
> = ({
  setBetTokenAmount,
  setIsAmountInvalid,

  disabled,
  maxButton,
  hotKey,
  isBlackListToken,
  hideEstToToken,
  updateBetAmountRef,
  placeholder,
  height,
  inputId,
  maxBet,
  ...props
}) => {
  const { states, controls, isValid, tokenAmount, estimateAmountInToken, balanceAmount } = useFormBetAmount({ maxBet })

  const selectedToken = useTokenSelected()
  const { hasSession, isSigned } = useAuth()
  const { t } = useTranslation()

  const isDisabled = !isSigned || disabled
  useEffect(() => {
    if (!isSigned) {
      controls.amount.clear()
    }
  }, [isSigned])

  useEffect(() => {
    setIsAmountInvalid(!isValid || states.amount.errors.length > 0)
  }, [isValid, states.amount.errors.length > 0])

  useEffect(() => {
    setBetTokenAmount(tokenAmount)
  }, [tokenAmount.token, tokenAmount.amount.toString(10)])

  useEffect(() => {
    if (updateBetAmountRef) updateBetAmountRef.current = controls.amount.onValueChanged
  }, [updateBetAmountRef])

  const onSelectTagAmount = useCallback(
    (multiplier: number) => {
      if (isDisabled) return

      const formatValue = new BigNumber(states.amount.value)
      const _amount = formatValue.gt(0) ? formatValue.multipliedBy(multiplier) : 0
      controls.amount.onValueChanged(_amount)
    },
    [states, controls, isDisabled],
  )
  const onMaxValue = useCallback(() => {
    if (isDisabled) return
    controls.amount.onValueChanged(balanceAmount.amount.toString(10))
  }, [states, controls, isDisabled, balanceAmount])

  const eventHandler = useCallback(
    (event) => {
      if (!hotKey) return null
      const key = event && event?.key?.toString()?.toUpperCase()

      if (key === 'S') {
        onSelectTagAmount(2)
      } else if (key === 'A') {
        onSelectTagAmount(0.5)
      } else if (key === 'D') {
        controls.amount.onValueChanged('0')
      }
    },
    [hotKey, controls, onSelectTagAmount],
  )

  useKeydownEventListener(eventHandler)

  return (
    <Flex flexDirection="column" {...props}>
      <StyledControlContainer state={states.amount} $disabled={isDisabled}>
        <RowBetween>
          <InHouseLabel>
            <Trans>Bet Amount</Trans>
          </InHouseLabel>
          {hasSession && !hideEstToToken && (
            <InHouseLabel>
              {estimateAmountInToken ? (
                <>
                  {getFullDisplayBalance(estimateAmountInToken, 0, 6)} {selectedToken.name}
                </>
              ) : (
                <Skeleton display="inline" minWidth="50px" ml="4px" minHeight="16px !important" />
              )}
            </InHouseLabel>
          )}
        </RowBetween>
        <StyledWrapInputAmount>
          <StyledInputContainer disabled={isDisabled} height={height}>
            <RowMiddle width="100%">
              <InHouseGameNumericalInput
                id={inputId}
                pattern="^[0-9]*[.,]?[0-9]?[0-9]?$"
                disabled={isDisabled}
                autoFocus={false}
                tabIndex={1}
                value={states.amount.value}
                validators={controls.amount.validators}
                onErrorChanged={controls.amount.onErrorChanged}
                onValueChanged={controls.amount.onValueChanged}
                validateOnchange
                placeholder={placeholder}
              />
              <Box pl="8px" pr="16px" minWidth={16}>
                <TokenLogo
                  token={tokenAmount.token}
                  size={16}
                  minWidth={16}
                  networkImageProps={{
                    width: 10,
                    height: 10,
                  }}
                />
              </Box>
            </RowMiddle>
          </StyledInputContainer>
          <StyledTagAmount $disabled={isDisabled} height={height} onClick={() => onSelectTagAmount(0.5)}>
            ½
          </StyledTagAmount>
          <StyledTagAmount $disabled={isDisabled} height={height} onClick={() => onSelectTagAmount(2)}>
            2×
          </StyledTagAmount>
          {maxButton && (
            <>
              <StyledTagAmount height={height} onClick={onMaxValue}>
                <Trans>Max</Trans>
              </StyledTagAmount>
            </>
          )}
        </StyledWrapInputAmount>
      </StyledControlContainer>

      {isBlackListToken && (
        <InputMessage color="error" textAlign="right" mb="4px">
          <GameInfoMessage
            variant="error"
            message={t('Playing games with {{tokenName}}({{network}}) has been suspended.', {
              tokenName: selectedToken.name,
              network: NETWORK_MAP[selectedToken.network].networkInfo.displayName,
            })}
          />
        </InputMessage>
      )}
      {!isDisabled && (
        <Text fontSize="10px" height="10px" color="error" mt="4px" textAlign="left">
          {states.amount.errors[0] === ValidationError.Insufficient && <Trans>Can't bet more than your balance</Trans>}
          {states.amount.errors[0] === ValidationError.GreatThan && (
            <Trans>Max bet amount is {{ max: getFullDisplayBalance(maxBet, 0, 2) }}$</Trans>
          )}
        </Text>
      )}
    </Flex>
  )
}

const StyledTagAmount = styled(RowCenter)<{ $disabled?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.5px;
  padding: 8px 16px;

  color: ${({ theme }) => theme.colors.text};
  border-radius: 0;
  background: ${({ theme }) => theme.colors.backgroundAlt5};
  cursor: pointer;
  position: relative;

  ${({ $disabled }) =>
    $disabled
      ? css`
          opacity: 0.5;
          box-shadow: none;
          color: ${({ theme }) => theme.colors.textSubtle};
          cursor: not-allowed;
        `
      : css`
          &:hover {
            transform: unset !important;
          }
        `}

  &:after {
    content: ' ';
    width: 1px;
    height: 16px;
    background: #313947;

    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(0, -50%);
  }
`

const StyledInputContainer = styled(InhouseGameInputContainer)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`
const StyledWrapInputAmount = styled(RowMiddle)`
  border-radius: ${({ theme }) => theme.radii.tiny};
  overflow: hidden;

  ${StyledTagAmount}:last-child {
    &:after {
      content: '';
      display: none;
    }
  }
`

export default BetAmount
